import { Dialog, DialogContent, Typography, Button, Box, useTheme } from '@mui/material';
import { FiAlertTriangle } from 'react-icons/fi';
import { useApproveOnboardingForm } from '@/apis/agency/businessOnboarding/hooks';
import { useToast } from '@/hooks/toast';

interface SetupBusinessModalProps {
  open: boolean;
  onClose: () => void;
  status:
    | 'draft'
    | 'collecting_agent_info'
    | 'in_review'
    | 'awaiting_engineering'
    | 'business_setup'
    | 'agent_preparation'
    | 'agent_qa_testing'
    | 'agent_finalized'
    | 'lost_on_onboarding'
    | 'completed';
  businessName: string;
  formId: number;
  businessPlanId?: string | number | null;
  voiceEngineId?: string | number | null;
  overallProgress?: number;
}

export default function SetupBusinessModal({
  open,
  onClose,
  status,
  businessName,
  formId,
  businessPlanId,
  voiceEngineId,
  overallProgress
}: SetupBusinessModalProps) {
  const theme = useTheme();
  const { showToast, ToastComponent } = useToast();
  const { mutate: approveOnboardingForm, isPending } = useApproveOnboardingForm();

  const handleApprove = () => {
    if (!businessPlanId || !voiceEngineId) {
      showToast('Pricing Package and Engine are required before setup', 'error');
      return;
    }

    approveOnboardingForm(String(formId), {
      onSuccess: () => {
        showToast('Business setup completed successfully', 'success');
        onClose();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to approve onboarding form';
        showToast(errorMessage, 'error');
      }
    });
  };

  const canSetupBusiness = status === 'in_review' || status === 'awaiting_engineering';

  // Case 1: Pending/Draft - Cannot setup business
  const isEarlyStage = status === 'draft' || status === 'collecting_agent_info';

  if (isEarlyStage) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              textAlign: 'center',
              px: 4,
              py: 2
            }
          }}
        >
          <DialogContent>
            <Typography fontWeight={500} fontSize={16} mb={2}>
              Cannot Setup Business
            </Typography>

            <Typography fontSize={14} color='#D97706' fontWeight={400} mb={3}>
              Application cannot be used to set up a business. Please wait for the customer to complete and submit their
              form for review.
            </Typography>

            <Box display='flex' justifyContent='center'>
              <Button
                variant='outlined'
                onClick={onClose}
                sx={{
                  borderRadius: '20px',
                  borderColor: '#777777',
                  color: '#777777',
                  px: 4,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        {ToastComponent}
      </>
    );
  }

  // Case 1.5: In Review but missing vital internal info
  if (!businessPlanId || !voiceEngineId) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              textAlign: 'center',
              px: 4,
              py: 2
            }
          }}
        >
          <DialogContent>
            <Typography fontWeight={500} fontSize={16} mb={2}>
              Cannot Setup Business
            </Typography>
            <Typography fontSize={14} color='#D97706' fontWeight={400} mb={3}>
              Pricing Package and Engine are not selected for this application. Please edit the form and fill the{' '}
              <strong>Internal Process</strong> section before setting up the business.
            </Typography>
            <Box display='flex' justifyContent='center'>
              <Button
                variant='outlined'
                onClick={onClose}
                sx={{
                  borderRadius: '20px',
                  borderColor: '#777777',
                  color: '#777777',
                  px: 4,
                  textTransform: 'none'
                }}
              >
                Close
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        {ToastComponent}
      </>
    );
  }

  // Case 2: In Review with incomplete progress - Show warning modal
  if (canSetupBusiness && overallProgress !== 100) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              textAlign: 'center',
              px: 4,
              py: 2
            }
          }}
        >
          <DialogContent>
            <Box display='flex' justifyContent='center' mb={2}>
              <Box
                sx={{
                  height: 72,
                  width: 72,
                  borderRadius: '50%',
                  backgroundColor: '#FFFBEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FiAlertTriangle size={32} color={theme.palette.warning.main} />
              </Box>
            </Box>

            <Typography fontWeight={500} fontSize={16} mb={2}>
              Incomplete Business Information
            </Typography>

            <Typography fontSize={14} color='text.secondary' fontWeight={400} mb={3}>
              Setting up without complete information means you&apos;ll need to{' '}
              <Typography component='span' fontWeight={500} color='common.black'>
                manually configure
              </Typography>{' '}
              several features later. This may require more time and effort to make everything fully functional.
            </Typography>

            <Box display='flex' justifyContent='end' gap={2}>
              <Button
                variant='outlined'
                onClick={handleApprove}
                disabled={isPending}
                sx={{
                  borderRadius: '20px',
                  borderColor: '#777777',
                  color: '#777777',
                  px: 4,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: theme.palette.secondary.light }
                }}
              >
                {isPending ? 'Approving...' : 'Setup Business Anyway'}
              </Button>
              <Button
                variant='contained'
                onClick={onClose}
                disabled={isPending}
                sx={{
                  borderRadius: '20px',
                  px: 4,
                  textTransform: 'none',
                  backgroundColor: theme.palette.warning.main
                }}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        {ToastComponent}
      </>
    );
  }

  // Case 3: In Review - Show confirmation modal
  if (canSetupBusiness) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              textAlign: 'center',
              px: 4,
              py: 2
            }
          }}
        >
          <DialogContent>
            <Typography fontWeight={500} fontSize={16} mb={2}>
              Business Setup
            </Typography>

            <Typography fontSize={14} color='text.secondary' fontWeight={400} mb={3}>
              Are you sure you want to setup a business for <strong>{businessName}</strong>?
              <br />
              This action will mark the onboarding as completed.
            </Typography>

            <Box display='flex' justifyContent='center' gap={2}>
              <Button
                variant='outlined'
                onClick={onClose}
                disabled={isPending}
                sx={{
                  borderRadius: '20px',
                  borderColor: '#777777',
                  color: '#777777',
                  px: 4,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={handleApprove}
                disabled={isPending || !canSetupBusiness}
                sx={{
                  borderRadius: '20px',
                  px: 4,
                  textTransform: 'none'
                }}
              >
                {isPending ? 'Approving...' : 'Business Setup'}
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
        {ToastComponent}
      </>
    );
  }

  // Case 4: Completed - Business already set up
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: 'center',
            px: 4,
            py: 2
          }
        }}
      >
        <DialogContent>
          <Typography fontWeight={500} fontSize={16} mb={2}>
            Business Already Set Up
          </Typography>

          <Typography fontSize={14} color='#077F13' fontWeight={400} mb={3}>
            This business is already set up. You can move forward with the application.
          </Typography>

          <Box display='flex' justifyContent='center'>
            <Button
              variant='outlined'
              onClick={onClose}
              sx={{
                borderRadius: '20px',
                borderColor: '#777777',
                color: '#777777',
                px: 4,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      {ToastComponent}
    </>
  );
}
